/**
 * Theme Definitions — dark-only. Light/normal palettes were removed when the app
 * went single-mode; ThemeMode is kept as a literal 'dark' for downstream type compat.
 */

import { MODAL_SCRIM_RGBA } from './modalOverlay.constants'

export interface ThemeColors {
  // Primary colors
  primary: string
  primaryLight: string
  primaryDark: string

  // Secondary colors
  secondary: string
  secondaryLight: string

  // Accent colors
  accent: string
  accentLight: string
  /** Darker gold/amber for odds and secondary accent; fits theme, not hardcoded. */
  accentDark: string

  // Background colors
  background: {
    primary: string
    card: string
    /**
     * Top app chrome (headers, tab strips): distinct from primary but still on-theme.
     * Light: soft violet-white; normal/dark: keep contrast with their shells.
     */
    shell: string
    /** Slightly elevated surface (e.g. cards inside a section). In dark mode, lighter than card for hierarchy. */
    cardElevated: string
    /** Dark inset surface (e.g. countdown timer strip). Dark in both themes for consistent contrast with gold/light text. */
    inset: string
    overlay: string
    /**
     * Open Board surfaces. The board's design is **purple-toned**, not the app's slate `card` —
     * `card` (#1F2937) is a blue-grey and reads wrong against the deep-purple `primary` shell.
     * Scoped to the board on purpose: the Arena's "My Challenges" cards stay on `card`, so do not
     * reach for these elsewhere without a design decision.
     */
    boardCard: string
    /** Inset panel inside a board card (pick/league/payout strip, clock pill). */
    boardInset: string
    /** Plinth the poster's avatar stands on, behind a rank-coloured ring. */
    boardPlinth: string
  }

  // Neutral colors
  white: string
  black: string
  gray: {
    50: string
    100: string
    200: string
    300: string
    400: string
    500: string
    600: string
    700: string
    800: string
    900: string
  }

  // Status colors
  success: string
  error: string
  /** Darker red for live/error gradients and depth (readable with theme.error). */
  errorDark: string
  warning: string
  info: string

  // Shadow colors
  shadow: string

  /** Text/icon color to render on top of theme.primary / theme.primaryDark fills (buttons, badges).
   *  Picked so contrast vs the dark-mode primary #A78BFA reaches WCAG AA body (4.5:1). */
  onPrimary: string

  /** Text on active leaderboard rows. Light in dark-only app so it's readable on cardActiveBg. */
  textOnCard: string
  /** Background for active leaderboard row; slightly lighter than card so text reads above ambient rows. */
  cardActiveBg: string
  /** Text on status badges (success/error/warning pills). Dark so readable on bright green/red/yellow. */
  textOnStatusBadge: string

  /** Countdown timer strip background. */
  timerInsetBg: string
  /** Countdown timer label/secondary text color (readable on timerInsetBg). */
  timerLabelColor: string
}

export const darkTheme: ThemeColors = {
  // Primary colors - Brighter purples for dark mode contrast
  primary: '#A78BFA', // Bright purple for dark backgrounds
  primaryLight: '#C4B5FD',
  primaryDark: '#8B5CF6',

  // Secondary colors - Brighter greens for dark mode
  secondary: '#6EE7B7',
  secondaryLight: '#A7F3D0',

  // Accent colors - Bright yellow for dark mode
  accent: '#FCD34D',
  accentLight: '#FDE68A',
  accentDark: '#F59E0B', // Darker amber for odds; readable on dark background

  // Background colors - Deep dark theme
  background: {
    primary: '#0F0A1A', // Deep dark purple/black background
    card: '#1F2937', // Dark gray card background - matches gray[100]
    shell: '#1F2937', // Same as card — subtle lift vs primary
    cardElevated: '#374151', // Slightly lighter (gray[200]) so cards read above section
    inset: '#111827', // Darker than card for timer strip; gold text pops
    overlay: MODAL_SCRIM_RGBA,
    boardCard: '#17122A', // Open Board card — purple-toned, per the Open Challenges design
    boardInset: '#110C20', // Panel inside a board card
    boardPlinth: '#241150', // Behind the poster's avatar
  },

  // Neutral colors - Inverted for dark mode (light text on dark)
  white: '#FFFFFF', // Keep white for icons/accents
  black: '#F7FAFC', // Light text color (inverted from light theme)
  gray: {
    50: '#111827', // Deepest gray (for the most subtle elevated surfaces)
    100: '#1F2937', // Darkest gray (for subtle backgrounds)
    200: '#374151', // Dark gray (for borders)
    300: '#4B5563', // Medium dark gray
    400: '#6B7280', // Medium gray
    500: '#9CA3AF', // Light gray (for secondary text)
    600: '#D1D5DB', // Lighter gray (for primary text on dark)
    700: '#E5E7EB', // Very light gray
    800: '#F3F4F6', // Almost white
    900: '#F9FAFB', // Nearly white (for high contrast text)
  },

  // Status colors - Brighter and more vibrant for dark mode
  success: '#6EE7B7', // Bright green
  error: '#F87171', // Bright red
  errorDark: '#DC2626', // Deeper red for live gradients on dark surfaces
  warning: '#FCD34D', // Bright yellow
  info: '#60A5FA', // Bright blue

  // Shadow colors
  shadow: 'rgba(0, 0, 0, 0.9)',

  // Foreground on primary fills. Same value as background.inset / textOnStatusBadge —
  // reused so dark "ink" reads from one source across buttons, badges, and chrome.
  onPrimary: '#111827',

  // Dark text on bright accent pills (accent yellow, secondary green, boost active state).
  // Despite the legacy name, this token does NOT colour the active leaderboard row —
  // that row applies theme.gray[900] (light) directly.
  textOnCard: '#1A202C',
  cardActiveBg: '#374151',
  textOnStatusBadge: '#111827',
  timerInsetBg: '#111827',
  timerLabelColor: '#9CA3AF',
}
